import {
  faUser,
  faCalendarLines,
  faUsers,
  faHeart,
  // faCartShopping,
  // faBullhorn,
  faMessageQuestion,
  faArrowRightFromBracket,
} from '@fortawesome/pro-light-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { useMemo } from 'react';
import { useStore } from 'zustand';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { Button } from '@/components/ui/button/button.component';
import { Icon } from '@/components/ui/icon.component';
import { ModalCloseButton } from '@/components/ui/modal-close-button/modal-close-button.component';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Link } from '@/components/ui/link/link.component';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { formatCustomerPreferredName } from '@/utils/formatters/format-customer-preferred-name';
import { formatUserInitials } from '@/utils/formatters/format-user-initials';
import { authStore } from '@/store/auth.store';
import { useLogout } from '@/hooks/use-logout.hook';
import { phonePatientSac } from '@/utils/consts/sac.consts';
import { useWhatsappLinkWithAnalytics } from '@/hooks/use-whatsapp/use-whatsapp-link-with-analytics.hook';

interface ProfileMenuItem {
  label: string;
  url: string;
  icon: IconDefinition;
  isNew: boolean;
  testId: string;
  target?: string;
  rel?: string;
  analyticsEvent?: string;
}

const ACCOUNT_ITEMS: ProfileMenuItem[] = [
  { label: 'Agendamentos', url: '/agendamentos', icon: faCalendarLines, isNew: false, testId: DataTestIdEnum.ITEM_APPOINTMENTS },
  { label: 'Perfil', url: '/meu-perfil', icon: faUser, isNew: false, testId: DataTestIdEnum.ITEM_PROFILE },
  { label: 'Dependentes', url: '/dependentes', icon: faUsers, isNew: false, testId: DataTestIdEnum.ITEM_DEPENDENTS },
  { label: 'Favoritos', url: '/favoritos', icon: faHeart, isNew: false, testId: DataTestIdEnum.ITEM_FAVORITES },
];

export function ProfileSheet() {
  const authenticatedUser = useStore(authStore, (state) => state.authenticatedUser);
  const logout = useLogout();
  const whatsappSacLink = useWhatsappLinkWithAnalytics(phonePatientSac, 'Olá! Sou paciente Medprev e preciso de suporte.');
  const whatsappSacUrl = whatsappSacLink.url;

  const menuItems = useMemo<ProfileMenuItem[]>(() => [
    ...ACCOUNT_ITEMS,
    // { label: 'Feedback', url: '/feedback', icon: faBullhorn, isNew: false, testId: DataTestIdEnum.ITEM_FEEDBACK },
    {
      label: 'Fale com a gente',
      url: whatsappSacUrl,
      icon: faMessageQuestion,
      isNew: false,
      target: '_blank',
      rel: 'noopener noreferrer',
      testId: DataTestIdEnum.ITEM_HELP,
      analyticsEvent: AnalyticsEnum.CLICK_PROFILE_MENU_TALK_TO_US,
    },
  ], [whatsappSacUrl]);

  const userItems = useMemo(() => {
    return {
      name: authenticatedUser ? formatCustomerPreferredName(authenticatedUser)?.split(' ')[0] : 'Visitante',
      thumbnail: null,
    };
  }, [authenticatedUser]);

  const initials = useMemo(
    () => (authenticatedUser ? formatUserInitials(formatCustomerPreferredName(authenticatedUser)) : ''),
    [authenticatedUser],
  );

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          data-testid={DataTestIdEnum.HEADER_USER_BUTTON}
          variant="secondary"
          color="primary"
          aria-label={initials ? `Perfil de ${userItems.name}` : 'Perfil'}
          className="w-8 h-8 min-w-8 p-0 rounded-full text-sm shrink-0"
        >
          {initials || <Icon icon={faUser} className="w-4 h-4" />}
        </Button>
      </SheetTrigger>
      <SheetContent
        aria-label="Menu da conta"
        aria-labelledby={undefined}
        title="Menu da conta"
        side="right"
        hasHeader
        className="w-full sm:w-[25rem] text-gray-800 text-lg overflow-y-auto p-0"
        data-testid={DataTestIdEnum.PROFILE_MENU_CONTENT}
      >
        <SheetHeader className="flex-row items-center gap-4 space-y-0 py-4 pl-6 pr-5">
          <SheetTitle data-testid={DataTestIdEnum.PROFILE_MENU_GREETING} className="flex-1 text-2xl font-normal text-gray-900">
            <span>Olá,</span>
            {' '}
            <span className="font-bold">
              {userItems.name}
              !
            </span>
          </SheetTitle>
          <SheetClose asChild>
            <ModalCloseButton />
          </SheetClose>
        </SheetHeader>

        <ul className="flex flex-col content-center !items-start">
          {menuItems.map((item) => (
            <li
              key={item.label}
              className="flex flex-row w-full"
            >
              <SheetClose asChild>
                <Link
                  data-testid={item.testId}
                  prefetch={false}
                  href={item.url}
                  target={item.target}
                  rel={item.rel}
                  data-analytics-event={item.analyticsEvent}
                  className="flex items-center gap-2 py-4 w-full px-6 text-gray-800 hover:bg-gray-100 focus-visible:bg-gray-50 focus-visible:text-success-700 transition-colors duration-300"
                >
                  <Icon icon={item.icon} className="w-5 h-4 shrink-0" />
                  <span className="text-lg font-normal">{item.label}</span>
                  {item.isNew && (
                    <div className="bg-green-100 text-success-700 text-base font-semibold border border-success-400 self-center px-2 rounded h-fit">
                      Novo
                    </div>
                  )}
                </Link>
              </SheetClose>
            </li>
          ))}
        </ul>
        <SheetFooter>
          <SheetClose
            data-testid={DataTestIdEnum.LOGOUT}
            onClick={logout}
            className="flex items-center gap-2 py-4 w-full px-6 text-left text-gray-800 hover:bg-gray-100 focus-visible:bg-gray-50 focus-visible:text-success-700 transition-colors duration-300"
          >
            <Icon icon={faArrowRightFromBracket} className="w-5 h-4 shrink-0" />
            <span className="text-lg">Sair</span>
          </SheetClose>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
